/**
 * Tooltip - Hover Hint Component
 *
 * Displays additional information on hover.
 * Follows the Eliza Forge design system.
 */

import * as React from "react"
import { cn } from "../../shared/lib/cn"

let tooltipWarmUntil = 0

interface TooltipProps {
  /** Tooltip content */
  content: React.ReactNode
  /** Element that triggers the tooltip */
  children: React.ReactElement
  /** Tooltip position */
  position?: "top" | "bottom" | "left" | "right"
  /** Delay before showing (ms) */
  delay?: number
  /** Additional class for tooltip */
  className?: string
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = "top",
  delay = 200,
  className,
}) => {
  const [mounted, setMounted] = React.useState(false)
  const [visible, setVisible] = React.useState(false)
  const [coords, setCoords] = React.useState({ x: 0, y: 0 })
  const triggerRef = React.useRef<HTMLElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement>(null)
  const timeoutRef = React.useRef<number>()
  const hideTimeoutRef = React.useRef<number>()

  const updatePosition = React.useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return

    const trigger = triggerRef.current.getBoundingClientRect()
    const tooltip = tooltipRef.current.getBoundingClientRect()
    const margin = 8

    let x = 0
    let y = 0

    switch (position) {
      case "top":
        x = trigger.left + trigger.width / 2 - tooltip.width / 2
        y = trigger.top - tooltip.height - margin
        break
      case "bottom":
        x = trigger.left + trigger.width / 2 - tooltip.width / 2
        y = trigger.bottom + margin
        break
      case "left":
        x = trigger.left - tooltip.width - margin
        y = trigger.top + trigger.height / 2 - tooltip.height / 2
        break
      case "right":
        x = trigger.right + margin
        y = trigger.top + trigger.height / 2 - tooltip.height / 2
        break
    }

    const maxX = Math.max(margin, window.innerWidth - tooltip.width - margin)
    const maxY = Math.max(margin, window.innerHeight - tooltip.height - margin)
    setCoords({
      x: Math.min(Math.max(margin, x), maxX),
      y: Math.min(Math.max(margin, y), maxY),
    })
  }, [position])

  const transformOrigin = React.useMemo(() => {
    switch (position) {
      case "top":
        return "bottom center"
      case "bottom":
        return "top center"
      case "left":
        return "right center"
      case "right":
        return "left center"
      default:
        return "center center"
    }
  }, [position])

  const showTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
    }

    const effectiveDelay = Date.now() < tooltipWarmUntil ? 0 : delay
    timeoutRef.current = window.setTimeout(() => {
      setMounted(true)
      window.requestAnimationFrame(() => {
        updatePosition()
        setVisible(true)
      })
      tooltipWarmUntil = Date.now() + 800
    }, effectiveDelay)
  }

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setVisible(false)
    tooltipWarmUntil = Date.now() + 800
    hideTimeoutRef.current = window.setTimeout(() => {
      setMounted(false)
    }, 160)
  }

  React.useLayoutEffect(() => {
    if (!mounted) return undefined

    updatePosition()
    const animationFrame = window.requestAnimationFrame(updatePosition)
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [mounted, updatePosition])

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  const child = React.cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: (e: React.MouseEvent) => {
      showTooltip()
      children.props.onMouseEnter?.(e)
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hideTooltip()
      children.props.onMouseLeave?.(e)
    },
    onFocus: (e: React.FocusEvent) => {
      showTooltip()
      children.props.onFocus?.(e)
    },
    onBlur: (e: React.FocusEvent) => {
      hideTooltip()
      children.props.onBlur?.(e)
    },
  })

  return (
    <>
      {child}
      {mounted && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className={cn(
            "pointer-events-none fixed z-[100] max-w-xs rounded-lg border border-black/5 bg-charcoal px-3 py-1.5 text-xs leading-5 text-white shadow-[0_12px_30px_rgba(15,23,42,0.16)]",
            "transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
            visible ? "opacity-100 scale-100" : "opacity-0 scale-[0.97]",
            "dark:border-white/10 dark:bg-gray-700 dark:text-gray-100",
            className
          )}
          style={{
            left: coords.x,
            top: coords.y,
            transformOrigin,
          }}
        >
          {content}
        </div>
      )}
    </>
  )
}
Tooltip.displayName = "Tooltip"

export { Tooltip }
export type { TooltipProps }
